import { useMemo } from "react";
import { theme } from "@chakra-ui/react";
import { FeatureCollection, Polygon, BBox, Point, GeoJsonProperties } from "geojson";
import { Layer, Source } from "react-map-gl";
import bboxPolygon from "@turf/bbox-polygon";

import { INFERENCES_CLASSES, getLikelySpecies } from "../utils";
import { Inference, InferenceCollection, InferenceProperties } from "../types/inferences";
import centroid from "@turf/centroid";

type Props = {
  data?: InferenceCollection
  id: string
}

type InferenceLayerProperties = InferenceProperties & {
  "species": string,
  "confidence": string,
  "centroid"?: number[],
}

function InferencesLayer({ data, id }: Props) {
  const polygonFeatures = useMemo((): FeatureCollection<Point | Polygon, InferenceLayerProperties> | undefined => {
    if (!data?.features) return undefined;

    return {
      type: "FeatureCollection",
      features: data.features
        .map((f: Inference) => {
          // In composite mode, use the actual Polygon geometry from API
          // In non-composite mode, create polygon from bbox
          const isComposite = f.properties.composite_id !== undefined;
          const geometry = isComposite && f.geometry.type === "Polygon"
            ? f.geometry
            : bboxPolygon(f.bbox as BBox).geometry;

          const centroidFeature = centroid({ type: "Feature", geometry, properties: {} });
          const { species, confidence } = getLikelySpecies(f);
          return {
            type: "Feature",
            geometry,
            properties: {
              ...f.properties,
              species,
              confidence,
              centroid: centroidFeature.geometry.coordinates,
            },
          };
        }),
    };
  }, [data]);

  const centroidFeatures = useMemo( () => {
    if (!polygonFeatures) return undefined;
    return {
      type: "FeatureCollection",
      features: polygonFeatures.features
        .map((f) => ({
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: f.properties?.centroid,
          },
          properties: f.properties,
        })),
    } as FeatureCollection<Point, GeoJsonProperties>;
  }, [polygonFeatures]);

  return (
    <>
      <Source id={`${id}-centroids`} type="geojson" data={centroidFeatures}>
        <Layer
          id={`${id}-centroids`}
          type="circle"
          maxzoom={12}
          paint={{
            "circle-color": [
              "match",
              ["get", "species"],
              ...Object.entries(INFERENCES_CLASSES).flatMap(([k, v]) => [k, v.color]),
              theme.colors.gray["500"], // fallback-color
            ],
            "circle-radius": {
              "base": 1.75,
              "stops": [
                [8, 2],
                [22, 180]
              ]
            },
          }}
        />
      </Source>
      <Source id={`${id}-polygons`} type="geojson" data={polygonFeatures}>
        <Layer
          id={`${id}-lines`}
          type="line"
          minzoom={11}
          paint={{
            "line-color": [
              "match",
              ["get", "species"],
              ...Object.entries(INFERENCES_CLASSES).flatMap(([k, v]) => [k, v.color]),
              theme.colors.gray["500"], // fallback-color
            ],
            "line-width":  [
              "interpolate",
              ["linear"], ["zoom"],
              0,
              0.125,
              22,
              4
            ],
            "line-opacity": [
              "interpolate",
              ["linear"], ["zoom"],
              0,
              0.125,
              22,
              0.75
            ],
          }}
        />
        <Layer
          id={`${id}-polygons`}
          type="fill"
          minzoom={11}
          paint={{
            "fill-color": [
              "match",
              ["get", "species"],
              ...Object.entries(INFERENCES_CLASSES).flatMap(([k, v]) => [k, v.color]),
              theme.colors.gray["500"], // fallback-color
            ],
            "fill-opacity": [
              "interpolate",
              ["linear"], ["zoom"],
              10,
              0.875,
              16,
              0
            ],
          }}
        />
      </Source>
    </>
  );
}

export default InferencesLayer;
